import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, of } from 'rxjs';
import { BotConfig } from '../interfaces';
import { API_URL } from '@/core/tokens';

@Injectable({
  providedIn: 'root',
})
export class BotConfigService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  public readonly config = signal<BotConfig | null>(null);
  public readonly loading = signal(false);
  public readonly error = signal<string | null>(null);

  private ready: Promise<void> = Promise.resolve();

  public load(botId: string): void {
    this.loading.set(true);
    this.error.set(null);

    this.ready = new Promise<void>((resolve) => {
      this.http
        .get<BotConfig>(`${this.apiUrl}/api/botconfig/${botId}/config`)
        .pipe(
          catchError((err) => {
            this.error.set((err as Error).message ?? 'Failed to load bot configuration');
            this.loading.set(false);
            resolve();
            return of(null);
          }),
        )
        .subscribe((cfg) => {
          if (cfg) {
            this.config.set(cfg);
            this.applyTheme(cfg);
          }
          this.loading.set(false);
          resolve();
        });
    });
  }

  /** Resolves once the current (or most recent) config load has settled, success or failure. */
  public whenReady(): Promise<void> {
    return this.ready;
  }

  // Writes all API color fields as CSS custom properties on <html>.
  // Every component reads these via var() — no prop-drilling needed.
  private applyTheme(cfg: BotConfig): void {
    const root = document.documentElement;
    const set = (prop: string, value: string | undefined) => {
      if (value) root.style.setProperty(prop, value);
    };

    // Direct API fields
    set('--bot-primary', cfg.primary_color);
    set('--bot-secondary', cfg.secondary_color);
    set('--bot-widget', cfg.widget_color);
    set('--bot-title', cfg.title_color);
    set('--bot-subtitle', cfg.subtitle_color);
    set('--bot-btn-content', cfg.button_content_color);

    // Derived: user bubble background uses a tint of primary
    set('--bot-bubble-bg', cfg.primary_color ? cfg.primary_color + '33' : undefined); // 20% alpha

    // Gradient overrides using primary + secondary
    if (cfg.primary_color && cfg.secondary_color) {
      const grad = `linear-gradient(90deg, ${cfg.primary_color} 0%, ${cfg.secondary_color} 100%)`;
      root.style.setProperty('--bot-gradient', grad);
    }
  }
}
